import { motion, AnimatePresence } from "framer-motion";
import { useRef } from "react";
import { ChevronUp } from "lucide-react";
import profilePhoto from "@/assets/profile-photo.webp";

const EDGE_LAYERS = 8;

interface ProfileHeaderProps {
  showHint?: boolean;
  onCoinClick?: (rect: DOMRect) => void;
}

const ProfileHeader = ({ showHint = false, onCoinClick }: ProfileHeaderProps) => {
  const coinRef = useRef<HTMLDivElement>(null);

  const handleCoinClick = () => {
    if (coinRef.current && onCoinClick) {
      onCoinClick(coinRef.current.getBoundingClientRect());
    }
  };

  const imgStyle: React.CSSProperties = {
    transform: 'scale(1.5) translateY(13%)',
    transformOrigin: 'center center'
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      onClick={handleCoinClick}
      className="flex items-start justify-between pb-2 cursor-pointer -mx-6 px-6 -mt-6 pt-6 sm:-mx-8 sm:px-8 sm:-mt-8 sm:pt-8">

      <div>
        <h1 className="text-[26px] sm:text-[32px] font-bold tracking-tight text-foreground leading-tight">Jacob Haas</h1>
        <p className="mt-1 text-[15px] sm:text-lg font-medium text-muted-foreground">Business, Entrepreneurship & AI</p>
      </div>

      <div className="relative flex flex-col items-center" style={{ width: 72 }}>
        <div
          ref={coinRef}
          className="h-[72px] w-[72px] shrink-0"
          style={{ perspective: '800px' }}>

          <div className="relative h-full w-full coin-3d" style={{ transform: 'rotateY(0deg)' }}>
            {/* Edge layers */}
            {Array.from({ length: EDGE_LAYERS }).map((_, i) => {
              const depth = (i + 1) * 2;
              const isOuter = i <= 1;
              const opacity = 0.35 - i * 0.03;
              return (
                <div
                  key={i}
                  className="absolute inset-0 rounded-full"
                  style={{
                    transform: `translateZ(${-depth}px)`,
                    background: `linear-gradient(135deg, hsla(200, 40%, 88%, ${opacity}), hsla(220, 30%, 92%, ${opacity * 0.7}))`,
                    boxShadow: isOuter ?
                    'inset 0 0 12px hsla(200, 50%, 95%, 0.7), inset 0 2px 4px hsla(0, 0%, 100%, 0.8), 0 0 6px hsla(220, 30%, 60%, 0.2)' :
                    'inset 0 0 6px hsla(200, 40%, 92%, 0.4), inset 0 1px 2px hsla(0, 0%, 100%, 0.3)',
                    backdropFilter: 'blur(3px)',
                    border: isOuter ? '1px solid hsla(0, 0%, 100%, 0.4)' : '0.5px solid hsla(0, 0%, 100%, 0.15)',
                    backfaceVisibility: 'hidden'
                  }} />);
            })}
            {/* Front face - B&W */}
            <div
              className="absolute inset-0 overflow-hidden rounded-full bg-muted"
              style={{ backfaceVisibility: 'hidden', transform: 'translateZ(2px)' }}>
              <img src={profilePhoto} alt="Jacob Haas" fetchPriority="high" className="h-full w-full object-cover" style={imgStyle} />
            </div>
          </div>
        </div>

        {/* Hint - centered below coin */}
        <AnimatePresence>
          {showHint &&
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="absolute w-full flex flex-col items-center coin-hint-bounce"
            style={{ top: 80 }}>
              <ChevronUp className="h-3 w-3 text-muted-foreground" />
              <span className="text-[10px] font-medium text-muted-foreground whitespace-nowrap">click me</span>
            </motion.div>
          }
        </AnimatePresence>
      </div>
    </motion.div>);
};

export default ProfileHeader;
